import { inject, reactive, onMounted, onUnmounted, nextTick } from "vue";
import { store } from "../store";
import { useRoute } from "vue-router";
import axiosLib from "axios";
import { useScrollState } from "@/composables/useScrollState";

export default function Common(name, { LoginURL, LogoutURL }) {
  const axios = inject("axios");
  const route = useRoute();

  // Set offset for scroll loading based on window height
  let offset = 12;
  const windowHeight = window.innerHeight;
  if (windowHeight > 1450) {
    offset = 24;
  } else if (windowHeight > 1100) {
    offset = 18;
  }
  const normalOffset = offset;

  const products = reactive({
    loading: false,
    onScrollLoading: false,
    data: [],
    error: "",
    pricelist_type: "retailer",
    isLoadingScrollData: false,
  });

  let currentSource = null;
  const CancelToken = axiosLib.CancelToken;

  let page = 1;
  let sort = "default";
  let stock = false;

  async function getFiltersData() {
    let dataToPost = {
      ...name,
      stock,
      min: 0,
      max: store.getters.getPriceRange,
    };
    await axios.api
      .get("/filter-options", { params: dataToPost })
      .then((res) => {
        let loggedIn = axios.isLoggedIn;
        let cat = res.data.data.categories.filter(c => (loggedIn || c.login_required == '0') && !c.name.includes("GGG"));
        let br = res.data.data.brands.filter(b => (loggedIn || b.login_required == '0') && !b.name.includes("GGG"));
        store.dispatch("updateOptions", cat);
        store.dispatch("updateBrandFilter", br);

        // if route has brand or category query param then select those options in filter badge 
        applyQuerySelections("category", cat, "category");
        applyQuerySelections("brand", br, "brand");
      })
      .catch(function(error) {
        console.log(error);
      });
  }

  // Select options from query params into filter badge
  const applyQuerySelections = (key, options, type) => {
    if (!route.query[key] || !Array.isArray(options)) return;

    const selectedIds = new Set(
      route.query[key]
        .toString()
        .split(",")
        .filter(Boolean)
    );

    const alreadySelectedIds = new Set(
      (store.getters.getSelected || []).map(opt => opt?.id?.toString()).filter(Boolean)
    );

    for (const option of options) {
      const id = option?.id?.toString();
      if (!id) continue;

      if (selectedIds.has(id) && !alreadySelectedIds.has(id)) {
        store.dispatch("selectedOption", { ...option, type });
        alreadySelectedIds.add(id); // prevents duplicates within the same run
      }
    }
  };

  async function getData(load) {
    let d = {};
    currentSource =  CancelToken.source();
    const brand = route.query.brand ? route.query.brand.split(",") : [];
    const category = route.query.category ? route.query.category.split(",") : [];
    const price = route.query.price || 0;
    stock = route.query.stock || false;
    sort = route.query.sort || (route.params.slug == "new-arrivals" ? "latest" : "");
    let dataToPost = {
      page,
      offset,
      stock,
      sort,
      brand,
      category,
      min: 0,
      max: price,
    };
    products[load] = true;
    if (axios.isLoggedIn) {
      await axios.authApi.get(LoginURL, { 
        params: dataToPost,
        cancelToken: currentSource.token,
      }).then((res) => {
        d = res.data.data;
        products.pricelist_type = res.data.pricelist_type || "retailer";
      }).catch(function(error) {
        if (axiosLib.isCancel(error)) {
          return;
        }
      });
    } else {
      await axios.api.get(LogoutURL, { params: dataToPost }).then((res) => {
        d = res.data.data;
      }).catch(function(error) {
        if (axiosLib.isCancel(error)) {
          return;
        }
      });
    }

    products[load] = false;
    return d;
  }
  const reset = () => {
    // Cancel previous request if still in progress
    if (currentSource) {
      currentSource.cancel("Canceled due to new request");
    }

    products.data = [];
    page = 1;
  };
  async function onSortChanged(v) {
    reset();
    sort = v;

    if(!sort && route.params.slug == "new-arrivals"){
      sort = 'latest';
    }

    store.dispatch("setSort", {
      previous: store.getters.getSort.current,
      current: sort || "default",
      by : sort,
      lastSlug: route.name == 'clearance' ? 'clearance' : route.params.slug 
    });

    // Temporary condition
    if(!sort && route.params.slug == "420-sale-collection"){
      store.dispatch("setIsRank", true);
      sort = 'popularity';
    }
    products.data = await getData("loading");
  }

  async function onStockChanged(v) {
    reset();
    stock = v;
    store.dispatch("setInStock", stock);
    // maxPrice(stock);
    store.dispatch("updateOptions", []);
    store.dispatch("updateBrandFilter", []);
    store.dispatch("removeOption", "clear");
    getFiltersData();
  }

  async function onFilterChanged(v) {
    const brand = store.getters.getSelected
      .filter((r) => r.type == "brand")
      .map((r) => r.id);
    const category = store.getters.getSelected
      .filter((r) => r.type == "category")
      .map((r) => r.id);
    reset();
    maxPrice(stock, category, brand);
    products.data = await getData("loading");
  }
  async function onPriceRangeChanged(v) {
    reset();
    products.data = await getData("loading");
    // store.dispatch("removeOption",'clear');
    // getFiltersData();
  }

  async function maxPrice(stock = null, category = [], brand = []) {
    let url = route.params.slug
      ? "/max-price?collection_slug=" + route.params.slug
      : "/max-price";
    await axios.authApi
      .get(url, {
        params: {
          stock: stock,
          brand: brand,
          category: category,
        },
      })
      .then((res) => {
        if (res.data.status) {
          store.dispatch("setPriceRange", Number(res.data.data.max_price));
          store.dispatch("setMaxPrice", Number(res.data.data.max_price));
          store.dispatch("setPriceCheck", false);
        }
      });
  }

  const scrollState = useScrollState();
  const storageKey = route.params.slug || name?.name || 'page';

  function saveScrollState() {
    scrollState.save(storageKey, {
      page,
      scrollY: window.scrollY,
    });
  }

  async function restoreScrollState() {
    const saved = scrollState.restore(storageKey);
    if (!saved) return false;

    if (saved.page > 1) {
      offset = saved.page * normalOffset;
      page = 1;
      const res = await getData("loading");
      offset = normalOffset;

      if (res?.data) {
        products.data = res;
        products.data.last_page = Math.ceil(res.total / normalOffset);
        products.data.current_page = saved.page;
        page = saved.page;
      }

      await nextTick();
      requestAnimationFrame(() => window.scrollTo({ top: saved.scrollY, behavior: 'smooth' }));
      return true;
    }

    return saved.scrollY;
  }

  async function handleScroll() {
    const itemsContainer = document.querySelector(".items-listing-container");
    if (!itemsContainer) {
      return;
    }
    const canLoadMore = page < products.data.last_page;
    const elemScrollHeight = itemsContainer.scrollHeight;
    const elemScrollLoadHeight = elemScrollHeight - elemScrollHeight * 0.5;
    const shouldLoadMore =
      window.pageYOffset + 200 > elemScrollLoadHeight ||
      elemScrollHeight < window.innerHeight ||
      window.pageYOffset + elemScrollLoadHeight < window.innerHeight;

    if (canLoadMore && shouldLoadMore && !products.isLoadingScrollData) {
      page++;
      products.isLoadingScrollData = true;
      const res = await getData("onScrollLoading");
      products.data.current_page = page;
      products.data.data?.push(...res.data);
      products.isLoadingScrollData = false;
    }
  }

  onMounted(() => {
    getFiltersData();
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("beforeunload", saveScrollState);
  });

  onUnmounted(() => {
    window.removeEventListener("scroll", handleScroll);
    window.removeEventListener("beforeunload", saveScrollState);
  });

  return {
    products,
    getFiltersData,
    getData,
    onSortChanged,
    onStockChanged,
    onFilterChanged,
    onPriceRangeChanged,
    maxPrice,
    reset,
    saveScrollState,
    restoreScrollState,
  };
}
